/**
 * Filiais (regionais) de uma empresa, dentro do detalhe da empresa — Nexora Grid (frontend, US-1902).
 *
 * Criado em: 19/10/2026 · Última modificação: 02/11/2026 · Versão: 1.2.0
 *
 * O código é a dimensão `filial` do tópico MQTT e do cadastro de dispositivos e não muda; o nome pode ser corrigido.
 * As filiais que já aparecem no hub ou nos dispositivos sem cadastro são listadas para regularizar. Filial em uso não
 * se exclui (inative); só a equipe Nexora altera.
 * v1.2 (US-4604, Sprint 46): bloco com título e contagem, tabela densa que rola dentro do bloco, formulário em linha com
 * validação ao sair do campo; mesmas chamadas e permissões.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, ErroApi } from "../api";
import { Campo, Esqueleto } from "../componentes/Tela";
import "./estrutura.css";

type Filial = { codigo: string; nome: string; ativa: boolean; versao: number; dispositivos: number; equipamentos_no_hub: number };
type Resposta = { filiais: Filial[]; nao_cadastradas: { codigo: string; dispositivos: number; equipamentos_no_hub: number }[] };
const CODIGO = /^[a-z0-9][a-z0-9_-]{0,63}$/;

export function Filiais({ empresa, edita, aoMudar }: { empresa: string; edita: boolean; aoMudar?: () => void }) {
  const [dados, setDados] = useState<Resposta | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [nova, setNova] = useState({ codigo: "", nome: "" });
  const [tocado, setTocado] = useState({ codigo: false, nome: false });
  const [renomeando, setRenomeando] = useState<{ codigo: string; nome: string } | null>(null);
  const base = `/v1/admin/empresas/${empresa}/filiais`;
  const carregar = useCallback(() => {
    api<Resposta>(base).then(setDados).catch(() => setDados({ filiais: [], nao_cadastradas: [] }));
  }, [base]);
  useEffect(carregar, [carregar]);
  const executar = async (acao: () => Promise<unknown>, ok: string) => {
    try { await acao(); setAviso(ok); carregar(); aoMudar?.(); }
    catch (e) { setAviso(e instanceof ErroApi ? `Não foi possível: ${e.message}` : "Falha de rede."); }
  };
  const criar = (e: FormEvent) => {
    e.preventDefault();
    void executar(() => api(base, { corpo: nova }), `Filial ${nova.codigo} cadastrada.`).then(() => { setNova({ codigo: "", nome: "" }); setTocado({ codigo: false, nome: false }); });
  };
  const renomear = (f: Filial) => {
    if (!renomeando) return;
    void executar(() => api(`${base}/${f.codigo}`, { metodo: "PUT", corpo: { versao: f.versao, nome: renomeando.nome } }), "Nome atualizado.")
      .then(() => setRenomeando(null));
  };
  const erroCodigo = tocado.codigo && !CODIGO.test(nova.codigo) ? "Use letras minúsculas, números, - ou _ (ex.: regional-norte)." : null;
  const erroNome = tocado.nome && nova.nome.trim().length < 2 ? "Escreva o nome da filial (2 letras ou mais)." : null;
  const ativas = dados?.filiais.filter((f) => f.ativa).length ?? 0;
  return (
    <section className="filiais tl-bloco admin-secao" aria-label="Filiais">
      <header>
        <h3>Filiais</h3>
        {dados && <span className="tl-subtitulo">{dados.filiais.length} cadastradas · {ativas} ativas</span>}
      </header>
      {aviso && <p className="admin-aviso" role="status">{aviso}</p>}
      {!dados ? <Esqueleto linhas={3} /> : (
        <>
          <div className="admin-tabela">
            <table className="tl-tabela">
              <thead><tr><th scope="col">Código</th><th scope="col">Nome</th><th scope="col">Situação</th><th scope="col">Dispositivos</th>
                <th scope="col">Equipamentos no hub (7 dias)</th>{edita && <th scope="col"><span className="visualmente-oculto">Ações</span></th>}</tr></thead>
              <tbody>
                {dados.filiais.map((f) => (
                  <tr key={f.codigo}>
                    <td className="tl-mono">{f.codigo}</td>
                    <td>{renomeando?.codigo === f.codigo
                      ? <input aria-label={`Novo nome da filial ${f.codigo}`} value={renomeando.nome} onChange={(e) => setRenomeando({ codigo: f.codigo, nome: e.target.value })} />
                      : f.nome}</td>
                    <td><span className={`nx-selo ${f.ativa ? "situacao-validado" : "situacao-rejeitado"}`}>{f.ativa ? "Ativa" : "Inativa"}</span></td>
                    <td className="tl-mono">{f.dispositivos}</td><td className="tl-mono">{f.equipamentos_no_hub}</td>
                    {edita && (
                      <td className="acoes-linha">
                        {renomeando?.codigo === f.codigo
                          ? <><button type="button" className="nx-botao botao-linha" onClick={() => renomear(f)}>Salvar</button>
                            <button type="button" className="tl-fantasma botao-linha" onClick={() => setRenomeando(null)}>Cancelar</button></>
                          : <button type="button" className="tl-fantasma botao-linha" onClick={() => setRenomeando({ codigo: f.codigo, nome: f.nome })}>Renomear</button>}
                        <button type="button" className="tl-fantasma botao-linha" onClick={() => void executar(() => api(`${base}/${f.codigo}/${f.ativa ? "inativar" : "reativar"}`, { corpo: {} }),
                          f.ativa ? "Filial inativada." : "Filial reativada.")}>{f.ativa ? "Inativar" : "Reativar"}</button>
                        {!f.dispositivos && !f.equipamentos_no_hub && (
                          <button type="button" className="tl-fantasma botao-linha" onClick={() => void executar(() => api(`${base}/${f.codigo}`, { metodo: "DELETE" }), "Filial excluída.")}>Excluir</button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                {!dados.filiais.length && <tr><td colSpan={6} className="vazio">Nenhuma filial cadastrada{edita ? ": cadastre a primeira abaixo." : "."}</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="admin-corpo">
            {dados.nao_cadastradas.length > 0 && (
              <div className="admin-pendentes">
                <b>Em uso e sem cadastro:</b>
                {dados.nao_cadastradas.map((n) => (
                  <span key={n.codigo} className="nao-cadastrada">
                    <span className="tl-mono">{n.codigo}</span> <small className="nx-suave">({n.dispositivos} disp. · {n.equipamentos_no_hub} no hub)</small>
                    {edita && <button type="button" className="tl-fantasma botao-linha" onClick={() => setNova({ codigo: n.codigo, nome: "" })}>Cadastrar</button>}
                  </span>
                ))}
              </div>
            )}
            {edita && (
              <form className="form-linha" onSubmit={criar} aria-label="Nova filial">
                <Campo id="filial-codigo" rotulo="Código da filial" erro={erroCodigo} ajuda="Não muda depois">
                  <input id="filial-codigo" required pattern="[a-z0-9][a-z0-9_\-]{0,63}" value={nova.codigo} aria-invalid={!!erroCodigo}
                    onBlur={() => setTocado({ ...tocado, codigo: true })}
                    onChange={(e) => setNova({ ...nova, codigo: e.target.value.trim().toLowerCase() })} placeholder="ex.: regional-norte" />
                </Campo>
                <Campo id="filial-nome" rotulo="Nome da filial" erro={erroNome}>
                  <input id="filial-nome" required minLength={2} value={nova.nome} aria-invalid={!!erroNome}
                    onBlur={() => setTocado({ ...tocado, nome: true })} onChange={(e) => setNova({ ...nova, nome: e.target.value })} />
                </Campo>
                <div className="tl-linha-acoes"><button type="submit" className="nx-botao">Cadastrar filial</button></div>
              </form>
            )}
          </div>
        </>
      )}
    </section>
  );
}
